import { FC, useMemo } from "react";
import styled from "@emotion/styled";
import {
  MDSActionMenuDropdown,
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuDropdownSlotName,
} from "@/mds";
import { clientEnvModule } from "@/modules/client-env";
import {
  getMemAgentHoldToTalkKeyDisplay,
  MEM_AGENT_HOLD_TO_TALK_KEYS,
  type MemAgentHoldToTalkKey,
  type MemAgentHoldToTalkKeySetting,
} from "@/modules/mem-agent/hold-to-talk-key";

export const MemAgentShortcutDropdown: FC<{
  value: MemAgentHoldToTalkKeySetting;
  onChange: (value: MemAgentHoldToTalkKey) => void;
  isDisabled?: boolean;
  isSaving?: boolean;
  isWindows?: boolean;
}> = ({ value, onChange, isDisabled = false, isSaving = false, isWindows }) => {
  const selectedDisplay = value ? getMemAgentHoldToTalkKeyDisplay(value, { isWindows }) : null;
  const triggerDisplay = value ? getShortcutTriggerDisplay(value, { isWindows }) : null;
  const menuContent = useMemo<MDSMenuContentList>(
    () =>
      MEM_AGENT_HOLD_TO_TALK_KEYS.map(key => {
        const display = getMemAgentHoldToTalkKeyDisplay(key, { isWindows });
        return {
          id: key,
          kind: MDSMenuContentItemKind.Item,
          label: display.label,
          icon: key === value ? MDSIconKind.Check : undefined,
          onClick: () => onChange(key),
        };
      }),
    [isWindows, onChange, value]
  );

  return (
    <MDSActionMenuDropdown contentList={menuContent} placement="bottom left">
      <ShortcutTriggerButton
        slot={MDSMenuDropdownSlotName.Trigger}
        aria-label={`${
          isSaving ? "Saving hold-to-talk key" : "Hold-to-talk key"
        }: ${selectedDisplay?.accessibleLabel ?? "Off"}`}
        isDisabled={isDisabled || isSaving}
        variant={MDSButtonVariant.Default}
      >
        {triggerDisplay ? (
          <ShortcutTriggerContent>
            {triggerDisplay.side && <span>{triggerDisplay.side}</span>}
            <ShortcutTriggerKey>{triggerDisplay.key}</ShortcutTriggerKey>
          </ShortcutTriggerContent>
        ) : (
          <ShortcutTriggerPlaceholder aria-hidden="true" />
        )}
        <MDSIcon kind={MDSIconKind.DropdownClosed} slot={MDSButtonSlotName.RightIcon} />
      </ShortcutTriggerButton>
    </MDSActionMenuDropdown>
  );
};

const getShortcutTriggerDisplay = (
  key: MemAgentHoldToTalkKey,
  { isWindows }: { isWindows?: boolean } = {}
): { side?: string; key: string } => {
  const platformIsWindows = isWindows ?? clientEnvModule.isWindows();

  switch (key) {
    case "tab":
      return { key: "Tab" };
    case "caps-lock":
      return { key: "Caps Lock" };
    case "right-shift":
      return { side: "Right", key: "Shift" };
    case "right-alt":
      return { side: "Right", key: platformIsWindows ? "Alt" : "Option" };
    case "right-ctrl":
      return { side: "Right", key: platformIsWindows ? "Ctrl" : "Control" };
    case "scroll-lock":
      return { key: "Scroll Lock" };
  }
};

const ShortcutTriggerButton = styled(MDSButton)`
  width: 144px;
  height: 40px;
  padding-inline: 12px;
  border-radius: 30px;
`;

const ShortcutTriggerContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;

const ShortcutTriggerKey = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${({ theme }) => theme.spacing[1]};
  border: 1px solid ${({ theme }) => theme.colors.border.onAccent};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
  white-space: nowrap;
`;

const ShortcutTriggerPlaceholder = styled.span`
  display: inline-flex;
  width: 32px;
  height: 32px;
`;
